import { useCallback, useEffect, useState } from 'react'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton, SkeletonGroup } from '@/components/ui/skeleton'
import { sha256 } from '../../lib/crypto'
import {
  type AttestationConsent,
  type AttestationScope,
  type AttestationTemplate,
  fetchAttestations,
} from '../../models/attestation'

// Cai's certification ends with this line too; as the checkbox's description it reaches agents that read only that.
const HUMAN_ONLY_NOTICE =
  'This certification may not be completed by an automated tool, software agent, or AI system.'

enum LoadStatus {
  LOADING = 'LOADING',
  ERROR = 'ERROR',
  READY = 'READY',
}

interface AttestationConsentProps {
  orgId: string
  /** Left out when `readonly`: nothing can be accepted there. */
  onConsentChange?: (allAccepted: boolean, consents: AttestationConsent[]) => void
  /** Narrows the templates to one surface; unset asks for every quote attestation. */
  scope?: AttestationScope
  /** Shows the wording with nothing to tick: staff read it, only the customer gives it. */
  readonly?: boolean
}

export function AttestationConsentComponent({
  orgId,
  onConsentChange,
  scope,
  readonly,
}: AttestationConsentProps) {
  const [templates, setTemplates] = useState<AttestationTemplate[]>([])
  const [consents, setConsents] = useState<Partial<Record<string, AttestationConsent>>>({})
  const [status, setStatus] = useState<LoadStatus>(LoadStatus.LOADING)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    fetchAttestations(orgId, scope)
      .then((loaded) => {
        if (cancelled) return
        setTemplates(loaded)
        setStatus(LoadStatus.READY)
      })
      .catch(() => {
        if (!cancelled) setStatus(LoadStatus.ERROR)
      })
    return () => {
      cancelled = true
    }
  }, [orgId, scope, attempt])

  // A failed fetch keeps the submit shut; only a fetch that found nothing to consent to opens it.
  useEffect(() => {
    if (status === LoadStatus.ERROR) onConsentChange?.(false, [])
    if (status === LoadStatus.READY && templates.length === 0) onConsentChange?.(true, [])
  }, [status, templates.length, onConsentChange])

  const notifyParent = useCallback(
    (updatedConsents: Partial<Record<string, AttestationConsent>>) => {
      const consentsList = Object.values(updatedConsents).filter(
        (c): c is AttestationConsent => c !== undefined
      )
      const allAccepted =
        templates.length > 0 &&
        templates.every((t) => updatedConsents[t.attestation_type]?.bool_value === true)
      onConsentChange?.(allAccepted, consentsList)
    },
    [templates, onConsentChange]
  )

  function handleToggle(template: AttestationTemplate, checked: boolean) {
    const consent: AttestationConsent = {
      attestation_type: template.attestation_type,
      version: template.version,
      content_hash: sha256(template.rendered_text),
      bool_value: checked,
      attested_at: new Date().toISOString(),
    }

    const updatedConsents = {
      ...consents,
      [template.attestation_type]: consent,
    }
    setConsents(updatedConsents)
    notifyParent(updatedConsents)
  }

  function retry() {
    setStatus(LoadStatus.LOADING)
    setAttempt((current) => current + 1)
  }

  if (status === LoadStatus.LOADING) {
    return (
      <SkeletonGroup aria-label="Loading the confirmation" className="border-t pt-4">
        <Skeleton className="h-24 w-full rounded-lg" />
      </SkeletonGroup>
    )
  }

  if (status === LoadStatus.ERROR) {
    return (
      <Banner
        variant="error"
        small
        action={
          <Button type="button" variant="outline" size="sm" onClick={retry}>
            Try again
          </Button>
        }
      >
        We couldn&apos;t load the confirmation.
      </Banner>
    )
  }

  if (templates.length === 0) {
    return null
  }

  return (
    <div className="space-y-4 border-t pt-4">
      {templates.map((template) => (
        <div key={template.attestation_type} className="bg-muted/50 flex items-start gap-3 rounded-lg p-3">
          <Checkbox
            id={`attestation-${template.attestation_type}`}
            aria-describedby={`attestation-${template.attestation_type}-notice`}
            checked={!readonly && consents[template.attestation_type]?.bool_value === true}
            disabled={readonly}
            onCheckedChange={(checked) => handleToggle(template, !!checked)}
            className="mt-0.5 size-5"
          />
          <div className="text-sm">
            {/* A label, not a <p>: it is the checkbox's accessible name and its hit area. */}
            <label
              htmlFor={`attestation-${template.attestation_type}`}
              className="block cursor-pointer whitespace-pre-wrap"
            >
              {template.rendered_text}
            </label>
            <p id={`attestation-${template.attestation_type}-notice`} className="sr-only">
              {HUMAN_ONLY_NOTICE}
            </p>
            {template.documents?.map((doc) => (
              <a
                key={doc.url}
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-ink hover:underline"
              >
                {doc.name}
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
